// Custom hook for offer calculations in modal
// Handles amount adjustments and field recalculations

import { useState, useCallback } from 'react';

// Configuration constants
export const OFFER_CALCULATION_CONFIG = {
  incrementStep: 500, // Amount to increment/decrement
  minimumAmount: 2000, // Minimum allowed gross advance amount
};

/**
 * Recalculates offer fields based on new gross advance amount
 * @param {Object} offer - The offer object
 * @param {number} newGrossAdvanceAmount - New gross advance amount
 * @returns {Object} - Updated offer with recalculated fields
 */
export const recalculateOfferFields = (offer, newGrossAdvanceAmount) => {
  const factor = offer.factor || 0;
  const termDuration = offer.term_duration_months || offer.term || 1;
  const processingFee = offer.processing_fee || 0;

  // Calculate NET PAYBACK: Gross Advance Amount * Factor
  const netPayback = newGrossAdvanceAmount * factor;

  // Calculate MONTHLY PAYMENT: (Gross Advance Amount * Factor) / Term Duration
  const monthlyPayment = netPayback / termDuration;

  // Calculate ADVANCE AMOUNT: Gross Advance Amount - FEE
  const advanceAmount = newGrossAdvanceAmount - processingFee;

  return {
    ...offer,
    gross_advance_amount: newGrossAdvanceAmount,
    grossAdvanceAmount: newGrossAdvanceAmount,
    net_payback: netPayback,
    netAdvanceAmount: netPayback,
    monthly_payment: monthlyPayment,
    monthlyGrossMargin: monthlyPayment,
    advance_amount: advanceAmount,
    advanceAmount: advanceAmount,
  };
};

/**
 * Validates if the new gross advance amount is within allowed limits
 * @param {Object} offer - The offer object
 * @param {number} newAmount - New amount to validate
 * @param {number} originalGrossAdvanceAmount - Original gross advance amount
 * @param {Object} config - Configuration object with incrementStep and minimumAmount
 * @returns {boolean} - True if amount is valid
 */
export const isValidGrossAdvanceAmount = (
  offer,
  newAmount,
  originalGrossAdvanceAmount,
  config = OFFER_CALCULATION_CONFIG
) => {
  const { incrementStep, minimumAmount } = config;

  // Amount must be greater than or equal to minimum
  if (newAmount < minimumAmount) {
    return false;
  }

  // Amount must be less than or equal to original
  if (newAmount > originalGrossAdvanceAmount) {
    return false;
  }

  // Amount must be a multiple of incrementStep
  if (newAmount % incrementStep !== 0) {
    return false;
  }

  return true;
};

/**
 * Custom hook for managing offer calculations in modal
 * @param {Array} initialOffers - Initial offers from modal
 * @param {Object} config - Configuration object with incrementStep and minimumAmount
 * @returns {Object} - Hook utilities and state
 */
const useOfferCalculations = (
  initialOffers = [],
  config = OFFER_CALCULATION_CONFIG
) => {
  // Store edited offers
  const [editedOffers, setEditedOffers] = useState({});

  // Store original gross advance amounts for validation
  const [originalAmounts, setOriginalAmounts] = useState({});

  /**
   * Initialize original amounts when offers are loaded
   */
  const initializeOffers = useCallback((offers) => {
    const amounts = {};
    offers.forEach(offer => {
      amounts[offer.id] = offer.gross_advance_amount || offer.grossAdvanceAmount || 0;
    });
    setOriginalAmounts(amounts);
    setEditedOffers({});
  }, []);

  /**
   * Get the current offer data (edited or original)
   */
  const getCurrentOffer = useCallback((offer) => {
    return editedOffers[offer.id] || offer;
  }, [editedOffers]);

  /**
   * Adjust gross advance amount by increment/decrement
   * If the calculated amount exceeds limits, it adjusts to the nearest valid limit
   */
  const adjustGrossAdvanceAmount = useCallback((offer, direction) => {
    const currentOffer = getCurrentOffer(offer);
    const currentGrossAdvanceAmount = 
      currentOffer.gross_advance_amount || currentOffer.grossAdvanceAmount || 0;
    
    const { incrementStep, minimumAmount } = config;
    const originalAmount = originalAmounts[offer.id] || currentGrossAdvanceAmount;
    
    let newAmount = 
      direction === 'increment' 
        ? currentGrossAdvanceAmount + incrementStep 
        : currentGrossAdvanceAmount - incrementStep;

    // If the new amount would exceed the original, set it to the original
    if (direction === 'increment' && newAmount > originalAmount) {
      newAmount = originalAmount;
    }
    
    // If the new amount would go below minimum, set it to the minimum
    if (direction === 'decrement' && newAmount < minimumAmount) {
      newAmount = minimumAmount;
    }

    // Only update if the amount is valid and different from current
    if (newAmount !== currentGrossAdvanceAmount && 
        isValidGrossAdvanceAmount(offer, newAmount, originalAmount, config)) {
      const updatedOffer = recalculateOfferFields(currentOffer, newAmount);
      setEditedOffers(prev => ({
        ...prev,
        [offer.id]: updatedOffer
      }));
    }
  }, [getCurrentOffer, originalAmounts, config]);

  /**
   * Check if increment is allowed
   * Returns true if current amount is below the original amount
   */
  const canIncrement = useCallback((offer) => {
    const currentOffer = getCurrentOffer(offer);
    const currentGrossAdvanceAmount = 
      currentOffer.gross_advance_amount || currentOffer.grossAdvanceAmount || 0;
    
    const originalAmount = originalAmounts[offer.id] || currentGrossAdvanceAmount;

    // Can increment if current amount is less than original
    return currentGrossAdvanceAmount < originalAmount;
  }, [getCurrentOffer, originalAmounts]);

  /**
   * Check if decrement is allowed
   * Returns true if current amount is above the minimum amount
   */
  const canDecrement = useCallback((offer) => {
    const currentOffer = getCurrentOffer(offer);
    const currentGrossAdvanceAmount = 
      currentOffer.gross_advance_amount || currentOffer.grossAdvanceAmount || 0;
    
    const { minimumAmount } = config;

    // Can decrement if current amount is greater than minimum
    return currentGrossAdvanceAmount > minimumAmount;
  }, [getCurrentOffer, config]);

  /**
   * Reset all edited offers
   */
  const resetEditedOffers = useCallback(() => {
    setEditedOffers({});
  }, []);

  /**
   * Get current gross advance amount for display
   */
  const getCurrentGrossAdvanceAmount = useCallback((offer) => {
    const currentOffer = getCurrentOffer(offer);
    return currentOffer.gross_advance_amount || currentOffer.grossAdvanceAmount || 0;
  }, [getCurrentOffer]);

  return {
    editedOffers,
    initializeOffers,
    getCurrentOffer,
    adjustGrossAdvanceAmount,
    canIncrement,
    canDecrement,
    resetEditedOffers,
    getCurrentGrossAdvanceAmount,
  };
};

export default useOfferCalculations;

